// @flow
import * as React from 'react'
import moment from 'moment'
import getPreciseDatetimeDiff from 'mz-utils/getPreciseDatetimeDiff'
import { FormattedMessage } from 'mz-intl'
import LandingIcon from 'mz-icons/airplane-landing'
import RiderIcon from 'mz-icons/rider'
import cloneDatetime from 'mz-utils/cloneDatetime'
import SelectField from '../../SelectField'
import messages from '../messages.intl'
import {
  Container,
  SelectWrapper,
  AdjustedTime,
  ExactTime,
  IconWrapper,
  Label,
  FieldContainer,
  EstimatedNote,
} from './elements'

// Types
export type Props = {
  rideDuration: number,
  freeWaitingMinutes: number,
  flightDatetime: string | moment,
  direction: 'arr' | 'dep',
  stepsCount: number,
}

// Constants
const DEPARTURE_TYPE = 'dep'
const ARRIVAL_TYPE = 'arr'
const OPTION_TIME_FORMAT = 'hh:mm A'
const DIRECTION_MAP = {
  [ARRIVAL_TYPE]: {
    Icon: LandingIcon,
    placeholderMsg: messages.RIGHT_AFTER_LANDING,
    labelMsg: messages.DRIVER_ARRIVE,
    optionMsg: messages.ARRIVE_TIME_SUFFIX,
    step: [15, 'minutes'],
    initStep: [0, 'minutes'],
  },
  [DEPARTURE_TYPE]: {
    Icon: RiderIcon,
    placeholderMsg: messages.SELECT_TIME_INTERVAL,
    labelMsg: messages.DRIVER_PICKUP,
    optionMsg: messages.PICKUP_TIME_SUFFIX,
    step: [-1, 'hours'],
    initStep: [-1, 'hours'],
  },
}

class RelativePickupDatetimeField extends React.Component<Props> {
  static defaultProps = {
    stepsCount: 12,
    rideDuration: 0,
    direction: ARRIVAL_TYPE,
    freeWaitingMinutes: 0,
  }

  getSearchParamsHash() {
    const { direction, flightDatetime, rideDuration, stepsCount } = this.props
    return `${direction}${flightDatetime}${rideDuration}${stepsCount}`
  }

  getOptions() {
    const { direction, flightDatetime, rideDuration, stepsCount } = this.props
    const { step, initStep } = DIRECTION_MAP[direction]
    const initOption = cloneDatetime(flightDatetime)
      .startOf('minutes')
      .add(...initStep)
    const result = [initOption]

    for (let i = 1; i < stepsCount; i++) {
      const nextTime = result[i - 1].clone().add(...step)
      result.push(nextTime)
    }

    if (direction === DEPARTURE_TYPE) {
      result.forEach((x) => x.add(-rideDuration, 'minutes'))
    }

    return result
  }

  getOptionDisplay(option: moment) {
    const { rideDuration, flightDatetime, direction } = this.props
    const flightDatetimeMoment = cloneDatetime(flightDatetime).startOf(
      'minutes'
    )
    const arrivalTime =
      direction === DEPARTURE_TYPE
        ? option.clone().add(rideDuration, 'minutes').startOf('minutes')
        : option.clone().startOf('minutes')
    const exactTime = option.format(OPTION_TIME_FORMAT)
    const diffTime = getPreciseDatetimeDiff(flightDatetimeMoment, arrivalTime)

    let timeMsg
    let exactTimeMsg = messages.EXACT_PICKUP_TIME
    if (flightDatetimeMoment.isSame(arrivalTime)) {
      timeMsg = messages.RIGHT_AFTER_LANDING
      exactTimeMsg = messages.RECOMMENDED_EXACT_PICKUP_TIME
    } else if (flightDatetimeMoment.isBefore(arrivalTime)) {
      timeMsg = messages.TIME_AFTER
    } else {
      timeMsg = messages.TIME_BEFORE
    }

    return { exactTime, diffTime, timeMsg, exactTimeMsg }
  }

  renderOptionCommon(option: moment, currColor: boolean) {
    const { direction } = this.props
    const { optionMsg } = DIRECTION_MAP[direction]
    const {
      exactTime,
      diffTime,
      timeMsg,
      exactTimeMsg,
    } = this.getOptionDisplay(option)

    return (
      <FormattedMessage
        message={optionMsg}
        values={{
          exactTime: (
            <ExactTime $currColor={currColor}>
              <FormattedMessage
                message={exactTimeMsg}
                values={{ time: exactTime }}
              />
            </ExactTime>
          ),
          amount: (
            <AdjustedTime $currColor={currColor}>
              <FormattedMessage message={timeMsg} values={{ time: diffTime }} />
            </AdjustedTime>
          ),
        }}
      />
    )
  }

  renderOption = (option: moment) => this.renderOptionCommon(option, true)

  renderValue = (option: moment) => this.renderOptionCommon(option, false)

  render() {
    const { direction, freeWaitingMinutes } = this.props
    const { Icon, labelMsg, placeholderMsg } = DIRECTION_MAP[
      this.props.direction
    ]

    return (
      <Container>
        <FieldContainer>
          <Label>
            <IconWrapper>
              <Icon fixedWidth />
            </IconWrapper>
            <FormattedMessage message={labelMsg} />
          </Label>
          <SelectWrapper>
            <SelectField
              {...this.props}
              key={this.getSearchParamsHash()}
              placeholder={<FormattedMessage message={placeholderMsg} />}
              renderOption={this.renderOption}
              renderValue={this.renderValue}
              options={this.getOptions()}
            />
          </SelectWrapper>
        </FieldContainer>
        {direction === DEPARTURE_TYPE && (
          <EstimatedNote>
            <FormattedMessage message={messages.ESTIMATED_ARRIVAL_NOTE} />
          </EstimatedNote>
        )}
        {direction === ARRIVAL_TYPE && freeWaitingMinutes > 0 && (
          <EstimatedNote>
            <FormattedMessage
              message={messages.FREE_WAITING_MINUTES}
              values={{ minutes: freeWaitingMinutes }}
            />
          </EstimatedNote>
        )}
      </Container>
    )
  }
}

export default RelativePickupDatetimeField
